import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import SlaCountdown from '../components/SlaCountdown';

const STATUS_STYLES: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-800',
  acknowledged: 'bg-sky-100 text-sky-800',
  assigned: 'bg-indigo-100 text-indigo-800',
  in_progress: 'bg-amber-100 text-amber-800',
  awaiting_citizen: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-700',
  reopened: 'bg-orange-100 text-orange-800',
};

const Chip: React.FC<{ value?: string }> = ({ value }) => (
  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[value || ''] || 'bg-gray-100 text-gray-600'}`}>
    {(value || '—').replaceAll('_', ' ')}
  </span>
);

// /files/* routes require the Bearer token everything else in this app
// sends via the axios interceptor — but an <img src> or <a href> is a plain
// browser navigation with no way to attach an Authorization header, so
// evidence thumbnails 401'd no matter what the backend returned. Fetching
// through apiService (which does attach the token) and handing the browser
// a blob: URL instead is the standard way around that without putting the
// token in a URL, where it could leak via history or logs.
const EvidenceThumb: React.FC<{ file: any }> = ({ file }) => {
  const [thumbUrl, setThumbUrl] = useState<string>();
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    apiService
      .get<Blob>(`/files/${file.id}/thumbnail`, { responseType: 'blob' })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setThumbUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  const open = async () => {
    setOpening(true);
    try {
      const blob = await apiService.get<Blob>(`/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Give the new tab a moment to actually load the blob before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      // eslint-disable-next-line no-alert
      alert('Could not open this file.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={opening}
      className="group relative block aspect-square overflow-hidden rounded-lg border bg-gray-50 text-left"
    >
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt={file.original_name || 'evidence'}
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
        />
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
        {file.original_name || file.kind || 'file'}
      </span>
    </button>
  );
};

/**
 * Unified case workspace: everything a supervisor needs about one report —
 * details, evidence, timeline, conversation, SLA clock and actions —
 * on a single screen.
 */
const CaseWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<any>();
  const [timeline, setTimeline] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [sla, setSla] = useState<any>();
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'internal'>('public');
  const [busy, setBusy] = useState('');
  const [pauseReason, setPauseReason] = useState('');

  const load = useCallback(async () => {
    setError('');
    const results = await Promise.allSettled([
      apiService.get(`/reports/${id}`),
      apiService.get(`/reports/${id}/timeline`),
      apiService.get(`/platform/reports/${id}/messages`),
      apiService.get(`/files/report/${id}`),
      apiService.get(`/platform/sla/${id}`),
    ]);
    const [r, t, m, f, s] = results;
    if (r.status === 'fulfilled') setReport((r.value as any).data);
    else setError('Unable to load this case.');
    if (t.status === 'fulfilled') setTimeline(((t.value as any).data as any[]) || []);
    if (m.status === 'fulfilled') setMessages(((m.value as any).data as any[]) || []);
    if (f.status === 'fulfilled') {
      const d: any = (f.value as any).data;
      setFiles(Array.isArray(d) ? d : d?.files || []);
    }
    if (s.status === 'fulfilled') setSla((s.value as any).data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>) => {
    setBusy(key);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.response?.data?.error?.message || 'Action failed.');
    } finally {
      setBusy('');
    }
  };

  const sendMessage = () =>
    act('message', async () => {
      if (!draft.trim()) return;
      await apiService.post(`/platform/reports/${id}/messages`, {
        body: draft.trim(),
        visibility,
      });
      setDraft('');
    });

  if (!report && !error) {
    return <div className="flex h-64 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/reports" className="text-sm text-primary-700 hover:underline">← Reports</Link>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{report?.reportNumber || report?.report_number || 'Case'}</h1>
            <Chip value={report?.status} />
          </div>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{report?.title}</p>
          {report?.detectedLanguage && report.detectedLanguage.toLowerCase() !== 'english' && report?.titleEn && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-primary-700 dark:text-primary-300">
              <span aria-hidden>✨</span> Written in {report.detectedLanguage} — English: "{report.titleEn}"
            </p>
          )}
        </div>
        <SlaCountdown
          dueAt={sla?.resolution_due_at}
          startedAt={sla?.started_at}
          paused={sla?.state === 'paused'}
          breachedAt={sla?.breached_at}
        />
      </div>

      {error && <div role="alert" className="rounded bg-red-50 p-3 text-red-700">{error}</div>}

      <div className="grid gap-4 xl:grid-cols-3">
        {/* Left: details */}
        <div className="space-y-4">
          <section className="card">
            <h2 className="mb-3 font-semibold">Details</h2>
            <dl className="space-y-2 text-sm">
              {[
                ['Category', report?.categoryName || report?.category_name],
                ['Priority', report?.priority],
                ['Ward', report?.wardNumber || report?.ward_number],
                ['Address', report?.address],
                ['Reported by', report?.reporterName || [report?.reporter_first_name, report?.reporter_last_name].filter(Boolean).join(' ')],
                ['Created', report?.createdAt || report?.created_at ? new Date(report?.createdAt || report?.created_at).toLocaleString() : ''],
                ['Assigned to', report?.assignedToName || report?.assigned_to_name || report?.technicianName],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="text-right font-medium text-gray-900 dark:text-gray-100 capitalize">{value || '—'}</dd>
                </div>
              ))}
            </dl>
            {report?.description && (
              <p className="mt-3 whitespace-pre-wrap border-t pt-3 text-sm text-gray-700 dark:text-gray-300">{report.description}</p>
            )}
            {report?.detectedLanguage && report.detectedLanguage.toLowerCase() !== 'english' && report?.descriptionEn && (
              <p className="mt-2 whitespace-pre-wrap rounded-sm border border-accent-500 bg-accent-50 p-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <span className="font-medium text-primary-900 dark:text-gray-100">English translation: </span>
                {report.descriptionEn}
              </p>
            )}
          </section>

          {/* SLA controls */}
          <section className="card">
            <h2 className="mb-3 font-semibold">SLA clock</h2>
            {!sla ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">No SLA clock is running for this case.</p>
                <button
                  onClick={() => act('sla', () => apiService.post(`/platform/sla/start/${id}`, {}))}
                  disabled={busy === 'sla'}
                  className="btn-primary text-sm"
                >Start SLA clock</button>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Policy</span><span className="font-medium">{sla.policy_name || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Due</span><span className="font-medium">{sla.resolution_due_at ? new Date(sla.resolution_due_at).toLocaleString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">State</span><span className="font-medium capitalize">{sla.state}</span></div>
                {sla.state === 'running' ? (
                  <div className="flex gap-2 pt-1">
                    <input
                      value={pauseReason}
                      onChange={(e) => setPauseReason(e.target.value)}
                      placeholder="Pause reason…"
                      className="input-field flex-1 text-sm"
                    />
                    <button
                      onClick={() => act('sla', () => apiService.post(`/platform/sla/${id}/pause`, { reason: pauseReason }))}
                      disabled={busy === 'sla' || !pauseReason.trim()}
                      className="btn-secondary text-sm"
                    >Pause</button>
                  </div>
                ) : sla.state === 'paused' ? (
                  <button
                    onClick={() => act('sla', () => apiService.post(`/platform/sla/${id}/resume`, {}))}
                    disabled={busy === 'sla'}
                    className="btn-primary text-sm"
                  >Resume clock</button>
                ) : null}
              </div>
            )}
          </section>

          {/* Evidence */}
          <section className="card">
            <h2 className="mb-3 font-semibold">Evidence ({files.length})</h2>
            {files.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No files attached.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {files.map((f: any) => (
                  <EvidenceThumb key={f.id} file={f} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Centre: timeline */}
        <section className="card xl:col-span-1">
          <h2 className="mb-4 font-semibold">Timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No events recorded yet.</p>
          ) : (
            <ol className="relative ml-2 space-y-5 border-l-2 border-gray-200 pl-5">
              {timeline.map((ev: any, i) => (
                <li key={ev.id || i} className="relative">
                  <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white bg-primary-600" />
                  <p className="text-sm font-semibold capitalize text-gray-900 dark:text-gray-100">
                    {(ev.title || ev.event_type || ev.type || '').toString().replaceAll('_', ' ')}
                  </p>
                  {(ev.description || ev.body) && (
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400">{ev.description || ev.body}</p>
                  )}
                  <p className="mt-0.5 text-xs text-gray-400">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                    {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Right: conversation */}
        <section className="card flex max-h-[75vh] flex-col xl:col-span-1">
          <h2 className="mb-3 font-semibold">Conversation</h2>
          <div className="scrollbar-thin flex-1 space-y-3 overflow-y-auto pr-1">
            {messages.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No messages yet.</p>}
            {messages.map((m: any) => {
              const citizen = m.sender_type === 'resident';
              const internal = m.visibility === 'internal';
              return (
                <div key={m.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${citizen ? 'bg-gray-100' : internal ? 'ml-auto border border-dashed border-amber-300 bg-amber-50' : 'ml-auto bg-primary-50'}`}>
                  <p className="whitespace-pre-wrap text-gray-800">{m.body}</p>
                  {m.translated_body && (
                    <p className="mt-1 whitespace-pre-wrap border-t border-gray-300 pt-1 text-xs italic text-gray-600">
                      {citizen ? 'English: ' : `Sent to citizen in ${m.detected_language || 'their language'}: `}{m.translated_body}
                    </p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-400">
                    {[m.first_name, m.last_name].filter(Boolean).join(' ') || m.sender_type}
                    {internal ? ' · internal note' : ''}
                    {m.created_at ? ` · ${new Date(m.created_at).toLocaleString()}` : ''}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-3 border-t pt-3">
            <div className="mb-2 flex gap-2 text-xs">
              {(['public', 'internal'] as const).map((v) => (
                <button key={v} onClick={() => setVisibility(v)}
                  className={`rounded-full px-3 py-1 capitalize ${visibility === v ? (v === 'internal' ? 'bg-amber-100 text-amber-800' : 'bg-primary-100 text-primary-800') : 'bg-gray-100 text-gray-500'}`}>
                  {v === 'public' ? 'Reply to citizen' : 'Internal note'}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={visibility === 'internal' ? 'Add an internal note…' : 'Write a reply to the citizen…'}
                className="input-field flex-1 text-sm"
              />
              <button onClick={sendMessage} disabled={busy === 'message' || !draft.trim()} className="btn-primary text-sm">Send</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CaseWorkspace;
